import { PACKAGES } from "#pages/data";
import {
  Badge,
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Icon,
  List,
  ListIcon,
  ListItem,
  Stack,
  Text,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React from "react";
import { FaCheckCircle, FaStar } from "react-icons/fa/index.js";

const MotionBox = motion(Box);

const ThreeTierPricing = () => {
  const headerColor = useColorModeValue("gray.900", "white");
  const textColor = useColorModeValue("gray.600", "gray.400");
  const cardBg = useColorModeValue("white", "gray.800");
  const featuredCardBg = useColorModeValue("blue.600", "blue.500");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Box py={{ base: 16, md: 24 }} id="pricing">
      <Container maxW="container.xl">
        <VStack spacing={4} mb={16} textAlign="center">
          <Badge
            colorScheme="blue"
            fontSize="sm"
            px={4}
            py={1}
            borderRadius="full"
            textTransform="uppercase"
            letterSpacing="widest"
          >
            Transparent Pricing
          </Badge>
          <Heading
            as="h2"
            fontSize={{ base: "3xl", md: "4xl" }}
            fontWeight="extrabold"
            color={headerColor}
            letterSpacing="-0.03em"
          >
            Choose your starting point.
          </Heading>
          <Text fontSize={{ base: "md", lg: "xl" }} color={textColor} maxW="container.md">
            Every package uses the same proven platform. The difference is scope, depth, and ongoing support.
            All prices shown are ranges — exact quotes follow your discovery session.
          </Text>
        </VStack>

        <Stack
          direction={{ base: "column", lg: "row" }}
          spacing={6}
          align="stretch"
          justify="center"
        >
          {PACKAGES.map((pkg, i) => {
            const isFeatured = pkg.highlighted;
            return (
              <MotionBox
                key={pkg.name}
                flex="1"
                maxW={{ lg: "380px" }}
                bg={isFeatured ? featuredCardBg : cardBg}
                color={isFeatured ? "white" : "inherit"}
                border="1px solid"
                borderColor={isFeatured ? "blue.400" : borderColor}
                borderRadius="2xl"
                overflow="hidden"
                boxShadow={isFeatured ? "2xl" : "md"}
                position="relative"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12 } as any}
                _hover={{ boxShadow: "2xl", transform: "translateY(-4px)" }}
                style={{ transition: "all 0.3s ease" }}
              >
                {isFeatured && pkg.badge && (
                  <Flex
                    bg="yellow.400"
                    color="gray.900"
                    fontSize="xs"
                    fontWeight="bold"
                    textTransform="uppercase"
                    letterSpacing="wider"
                    py={1.5}
                    px={4}
                    align="center"
                    justify="center"
                    gap={1}
                  >
                    <Icon as={FaStar} boxSize={3} />
                    {pkg.badge}
                  </Flex>
                )}

                <VStack spacing={0} align="stretch">
                  {/* Header */}
                  <Box p={8} pb={6}>
                    <Heading
                      as="h3"
                      fontSize="2xl"
                      fontWeight="extrabold"
                      mb={2}
                    >
                      {pkg.name}
                    </Heading>
                    <Text
                      fontSize="sm"
                      opacity={isFeatured ? 0.85 : 0.7}
                      mb={6}
                    >
                      {pkg.tagline}
                    </Text>
                    <Box>
                      <Text
                        fontSize="3xl"
                        fontWeight="extrabold"
                        lineHeight="1"
                      >
                        {pkg.price}
                      </Text>
                      <Text
                        fontSize="sm"
                        opacity={0.65}
                        mt={1}
                      >
                        {pkg.billing === "monthly" ? "billed monthly, cancel anytime" : "one-time investment"}
                      </Text>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Box
                    borderTop="1px solid"
                    borderColor={isFeatured ? "blue.400" : borderColor}
                    opacity={isFeatured ? 0.3 : 0.5}
                  />

                  {/* Features */}
                  <Box p={8}>
                    <Text
                      fontSize="xs"
                      fontWeight="bold"
                      textTransform="uppercase"
                      letterSpacing="wider"
                      opacity={0.6}
                      mb={4}
                    >
                      What&apos;s included
                    </Text>
                    <List spacing={3} mb={8}>
                      {pkg.features.map((feature) => (
                        <ListItem key={feature} fontSize="sm" display="flex" alignItems="flex-start">
                          <ListIcon
                            as={FaCheckCircle}
                            color={isFeatured ? "yellow.300" : "green.400"}
                            mt={0.5}
                            flexShrink={0}
                          />
                          {feature}
                        </ListItem>
                      ))}
                    </List>

                    <Button
                      as="a"
                      href={pkg.ctaHref}
                      size="lg"
                      width="full"
                      colorScheme={isFeatured ? "yellow" : "blue"}
                      variant={isFeatured ? "solid" : "outline"}
                      color={isFeatured ? "gray.900" : undefined}
                      fontWeight="bold"
                      borderRadius="xl"
                      _hover={{
                        transform: "translateY(-2px)",
                        boxShadow: "lg",
                      }}
                      transition="all 0.2s"
                    >
                      {pkg.cta}
                    </Button>
                  </Box>
                </VStack>
              </MotionBox>
            );
          })}
        </Stack>

        {/* Bottom Note */}
        <Box textAlign="center" mt={12}>
          <Text fontSize="sm" color={textColor}>
            All packages include a discovery session. Pricing is finalized after we understand your specific process.{" "}
            <Box
              as="a"
              href="/contact-us?interest=discovery"
              color="blue.500"
              fontWeight="semibold"
              _hover={{ textDecoration: "underline" }}
            >
              Book a free 30-min call
            </Box>{" "}
            to get an exact quote.
          </Text>
        </Box>
      </Container>
    </Box>
  );
};

export default ThreeTierPricing;
